import { useEffect, useState } from "react";
import { NAV_LINKS } from "@/lib/site";
import { Menu, X } from "lucide-react";

const Logo = () => (
  <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <span className="relative grid place-items-center h-9 w-9 rounded-xl bg-lark-blue shadow-[0_6px_16px_rgba(22,119,255,0.35)]">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M4 20V4l7 8 2-3 7 11H4Z" fill="white" />
      </svg>
    </span>
    <span className="font-display text-lg font-semibold tracking-tight text-lark-ink">
      Lark<span className="text-lark-blue">.</span>
    </span>
  </a>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-gray-200/70 shadow-[0_4px_24px_rgba(15,23,42,0.04)]"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-lark-muted hover:text-lark-ink transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:block">
          <a
            href="#contact"
            data-testid="nav-contact-cta"
            className="inline-flex items-center rounded-full bg-lark-blue px-5 py-2.5 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(22,119,255,0.30)] hover:bg-lark-blueDark hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
          >
            Contact Us
          </a>
        </div>
        <button
          className="md:hidden grid place-items-center h-10 w-10 rounded-lg text-lark-ink"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-2.5 text-base font-medium text-lark-ink"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex w-full justify-center rounded-full bg-lark-blue px-5 py-3 text-sm font-semibold text-white"
          >
            Contact Us
          </a>
        </div>
      )}
    </header>
  );
};
